import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useProjectCollectionFragment } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { Emoji } from '@frameio/shared/emoji';
import { getLink } from '@frameio/shared/routing';
import { consumeEvent } from '@frameio/utils';
import {
  Box,
  IconButton2,
  mergeProps,
  theme,
  useFocusWithin,
} from '@frameio/vapor';
import {
  IconCollections,
  IconCollectionsLock,
  IconMoreHorizontal,
} from '@frameio/vapor-icons';
import {
  useBaseDepth,
  useIsActiveItem,
  useIsContextMenuDisabled,
  useOnItemSelect,
  useOpenNavigationMenu,
} from '../../contexts';
import type { CollectionTreeItemProps, ProjectTreeItem } from '../../types';
import {
  BaseContainer,
  calculatePadding,
  ICON_WIDTH,
  ItemContainer,
} from '../TreeItem';
import { CollectionName } from './CollectionName';
import TEXT from './generated/TEXT/ProjectTree_Collection';

export function Collection({
  depth,
  id,
  transition,
  type,
}: CollectionTreeItemProps) {
  const { data: collection } = useProjectCollectionFragment(id);
  const baseDepth = useBaseDepth();
  const onItemSelect = useOnItemSelect();
  const isActive = useIsActiveItem({ id, type });
  const isDisabled = transition === 'close';
  const projectId = useActiveProjectId();
  // shift collections depth by 1 to indent them all
  const paddingLeft = calculatePadding(baseDepth, depth + 1);
  const isIconEnabled = collection?.appearance?.header?.isIconEnabled || true;
  const emoji = collection?.appearance?.header?.iconEmoji;

  const contextMenuDisabled = useIsContextMenuDisabled();
  const iconRef = useRef<HTMLButtonElement | null>(null);
  const openNavigationMenu = useOpenNavigationMenu();

  const [focused, setFocused] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    isDisabled,
    onFocusWithinChange: setFocused,
  });

  const href = useMemo(
    () =>
      getLink({
        collectionId: id,
        page: 'collection',
        projectId,
      }),
    [id, projectId]
  );

  const item = useMemo<ProjectTreeItem>(() => ({ id, type }), [id, type]);

  const handleContextMenu = (event?: React.MouseEvent) => {
    event?.preventDefault();

    if (contextMenuDisabled) return false;

    openNavigationMenu(item, iconRef, event);

    return true;
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    let didHandleEvent = false;

    if (event.key === 'Enter') {
      onItemSelect(item, href);
      didHandleEvent = true;
    }

    if (event.key === 'ContextMenu') {
      handleContextMenu();
      didHandleEvent = true;
    }

    if (didHandleEvent) {
      consumeEvent(event);
    }
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button === 2 || event.metaKey) return;

      onItemSelect(item, href);
    },
    [href, item, onItemSelect]
  );

  const mergedHandlers = mergeProps(focusWithinProps, {
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    onContextMenu: handleContextMenu,
  });

  const FallbackIcon = collection.private
    ? IconCollectionsLock
    : IconCollections;

  return (
    <BaseContainer key={id} $isActive={isActive} {...mergedHandlers}>
      <ItemContainer
        adornmentAfter={
          <IconButton2
            ref={iconRef}
            icon={<IconMoreHorizontal />}
            label={TEXT.moreOptions}
            onPress={() => handleContextMenu()}
            size="small"
            variant="tertiary"
          />
        }
        expandable={false}
        id={id}
        isActive={isActive}
        isDisabled={isDisabled}
        isLink={true}
        isLoading={false}
        isOpen={true}
        name={collection.name}
        paddingLeft={paddingLeft}
        type={type}
      >
        {isIconEnabled && emoji ? (
          <CollectionEmojiIcon isPrivate={collection.private ?? false}>
            <Emoji emoji={emoji} size={EMOJI_SIZE} />
          </CollectionEmojiIcon>
        ) : (
          <FallbackIcon
            color={theme.colors.icon.primary}
            size={ICON_WIDTH}
            style={{ flexShrink: 0 }}
          />
        )}
        <CollectionName focused={focused} isActive={isActive} item={item} />
      </ItemContainer>
    </BaseContainer>
  );
}

const EMOJI_SIZE = 18;

TEXT.DEFINE({
  /** aria-label for the more options button for collections in the project tree. */
  moreOptions: 'More options',
});

type CollectionIconProps = {
  children: React.ReactNode;
  isPrivate: boolean;
};

const CollectionEmojiIcon = ({ children, isPrivate }: CollectionIconProps) => (
  <Box
    display="inline"
    position="relative"
    style={{ marginLeft: theme.spaces[4], marginRight: theme.spaces[10] }}
  >
    {children}
    {isPrivate && (
      /**
       * @todo (VAPR-1990): This inline svg temporarily addresses the
       * need to indicate when a collection w/ custom emoji icon is set to private.
       * Vapor is working on a more sustainable solution.
       */
      <svg
        fill="none"
        height="10"
        style={{
          position: 'absolute',
          bottom: '-1px',
          right: '-1px',
        }}
        viewBox="0 0 8 10"
        width="8"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_203_10714)">
          <path
            clipRule="evenodd"
            d="M2 3.99951V2.89317C2 2.63721 2.05474 2.38513 2.15952 2.15181C2.26424 1.91865 2.41617 1.71024 2.60399 1.53701C2.79171 1.36388 3.01188 1.22895 3.25076 1.13769C3.48961 1.04644 3.74406 1 4 1C4.25594 1 4.51039 1.04644 4.74924 1.13769C4.98812 1.22895 5.20829 1.36388 5.39601 1.53701C5.58383 1.71024 5.73576 1.91865 5.84048 2.15181C5.94526 2.38513 6 2.63721 6 2.89317V3.99951C6.55228 3.99951 7 4.44723 7 4.99951V7.99951C7 8.5518 6.55228 8.99951 6 8.99951H2C1.44771 8.99951 1 8.5518 1 7.99951V4.99951C1 4.44723 1.44771 3.99951 2 3.99951ZM3.69686 2.30538C3.79166 2.26916 3.8948 2.25 4 2.25C4.1052 2.25 4.20834 2.26916 4.30314 2.30538C4.3979 2.34158 4.48089 2.39348 4.54854 2.45587C4.61608 2.51816 4.6667 2.58935 4.70019 2.66392C4.73361 2.73832 4.75 2.81606 4.75 2.89317V3.99951H3.25V2.89317C3.25 2.81606 3.26639 2.73832 3.29981 2.66392C3.3333 2.58935 3.38392 2.51816 3.45146 2.45587C3.51911 2.39348 3.6021 2.34158 3.69686 2.30538Z"
            fill={theme.colors.icon.primarySolid}
            fillRule="evenodd"
          />
          <path
            clipRule="evenodd"
            d="M5.84048 2.15181C5.94526 2.38513 6 2.63721 6 2.89317V3.99951C6.55228 3.99951 7 4.44723 7 4.99951V7.99951C7 8.5518 6.55228 8.99951 6 8.99951H2C1.44771 8.99951 1 8.5518 1 7.99951V4.99951C1 4.44723 1.44771 3.99951 2 3.99951V2.89317C2 2.63721 2.05474 2.38513 2.15952 2.15181C2.26424 1.91865 2.41617 1.71024 2.60399 1.53701C2.79171 1.36388 3.01188 1.22895 3.25076 1.13769C3.48961 1.04644 3.74406 1 4 1C4.25594 1 4.51039 1.04644 4.74924 1.13769C4.98812 1.22895 5.20829 1.36388 5.39601 1.53701C5.58383 1.71024 5.73576 1.91865 5.84048 2.15181ZM3.29981 2.66392C3.26639 2.73832 3.25 2.81606 3.25 2.89317V3.99951H4.75V2.89317C4.75 2.81606 4.73361 2.73832 4.70019 2.66392C4.6667 2.58935 4.61608 2.51816 4.54854 2.45587C4.48089 2.39348 4.3979 2.34158 4.30314 2.30538C4.20834 2.26916 4.1052 2.25 4 2.25C3.8948 2.25 3.79166 2.26916 3.69686 2.30538C3.6021 2.34158 3.51911 2.39348 3.45146 2.45587C3.38392 2.51816 3.3333 2.58935 3.29981 2.66392ZM1 3.26708V2.89317C1 2.49412 1.08542 2.10257 1.24729 1.74214C1.40898 1.38213 1.64197 1.0639 1.92602 0.801924C2.2098 0.540192 2.53972 0.338843 2.89388 0.20354C3.24796 0.0682717 3.62347 0 4 0C4.37653 0 4.75203 0.0682666 5.10611 0.203535C5.46027 0.338838 5.79019 0.540187 6.07398 0.801919C6.35802 1.06389 6.591 1.38209 6.7527 1.7421C6.91456 2.10253 7 2.49412 7 2.89317V3.26708C7.5978 3.61289 8 4.25923 8 4.99951V7.99951C8 9.10409 7.10456 9.99951 6 9.99951H2C0.895434 9.99951 0 9.10409 0 7.99951V4.99951C0 4.25923 0.402198 3.61289 1 3.26708Z"
            fill={theme.colors.background.panel}
            fillRule="evenodd"
          />
        </g>
        <defs>
          <clipPath id="clip0_203_10714">
            <rect fill="white" height="10" width="8" />
          </clipPath>
        </defs>
      </svg>
    )}
  </Box>
);
